import type { ChatActions } from 'flare-chat-core/controller';
import type { ChatOperationResult, ChatState, SessionRecord } from 'flare-chat-core/contracts';
import type { ProductSendInput, UploadFileInput } from './contracts';
import { buildHostInputResponse } from '../../adapters/chat/input-response.js';
import { selectCurrentComposerInputProjection } from '../../projection/composer/composerUiProjectionSelector.js';
import { canonicalContext } from '../../projection/canonical.js';
import { attachmentInput } from '../../adapters/chat/attachments.ts';
import { buildHostSendInput } from '../../adapters/chat/send-input.ts';
import { createMessageCapabilityAction } from '../actions/message-capability';
import type { SkillReadOwner } from '../capabilities/contracts';
import type { InputResponse } from '../operations/input-response';

const value = async <T>(promise: Promise<ChatOperationResult<T>>) => {
  const result = await promise;
  if (result.status === 'completed') return result.value;
  const error = 'error' in result ? result.error : undefined;
  const reason = 'reason' in result ? result.reason : result.status;
  throw Object.assign(new Error(error?.message ?? reason), error ?? {}, { operationResult: result });
};
const identity = (record: SessionRecord) => String(record?.sessionId ?? record?.session_id ?? record?.id ?? '');
export const createHostActions = (actions: ChatActions, getSnapshot: () => ChatState, options: {
  defaultSessionTitle?: string; userId?: string; skills?: SkillReadOwner; onSessionReset?: () => void;
}) => {
  // A successful public reset starts a new presentation draft, not a new canonical Session.
  const resetSession = () => {
    const result = actions.resetSession();
    if (result.status === 'completed') options.onSessionReset?.();
    return result;
  };
  return ({
  sessionList: {
    onSelectSession: (id: string) => value(actions.selectSession(id)),
    onCreateSession: resetSession,
    onLoadMore: () => value(actions.loadMoreSessions()),
    onSortSessions: (sortOrder: 'asc' | 'desc') => value(actions.sortSessions!(sortOrder)),
    onSearch: (query: string) => value(actions.searchSessions(query)),
    onRenameSession: async (record: SessionRecord, title: string) => { await value(actions.updateTitle(identity(record), title)); return value(actions.refreshSessionList()); },
    onArchiveSession: async (record: SessionRecord) => {
      const id = identity(record);
      await value(actions.archiveSession(id));
      if (getSnapshot().session.sessionId !== id) return;
      const next = getSnapshot().sessions.items.find((item) => item.status !== 'archived' && identity(item) !== id);
      if (next) return value(actions.selectSession(identity(next)));
      resetSession();
    },
  },
  session: { onRefresh: () => {
    const id = getSnapshot().session.sessionId;
    return id ? value(actions.loadSession(id)) : Promise.resolve(null);
  } },
  timeline: { onLoadOlder: () => value(actions.loadOlderMessages()) },
  composer: {
    userId: options.userId,
    onMessageCapabilityAction: createMessageCapabilityAction({ actions, getSnapshot, skills: options.skills }),
    onRespondInput: async (input: InputResponse) => {
      const snapshot = getSnapshot();
      const response = buildHostInputResponse(input, { sessionId: snapshot.session.sessionId,
        currentRequest: selectCurrentComposerInputProjection({ sessionId: snapshot.session.sessionId,
          sessionContext: canonicalContext(snapshot), run: snapshot.session.detail?.active_run ?? snapshot.session.detail?.latest_run }).inputRequest, userId: options.userId });
      const result = await actions.executeOperation(response);
      const current = getSnapshot();
      const canClear = [input.response_kind === 'message', result.status === 'completed',
        current.session.sessionId === response.sessionId,
        current.composer.inputValue === snapshot.composer.inputValue,
        snapshot.composer.inputValue === input.message].every(Boolean);
      if (canClear) actions.setInputValue('');
      if ('error' in result && result.error.code === 'AUTO_INPUT_CONFLICT' && current.session.sessionId === response.sessionId) {
        await actions.loadSession(response.sessionId);
      }
      return result;
    },
    onChange: (event: { target?: { value?: string } }) => actions.setInputValue(event?.target?.value ?? ''),
    onSend: (input: ProductSendInput = {}) => actions.send(buildHostSendInput(input)),
    onAbort: actions.abort,
    onAttachFiles: (files: UploadFileInput[]) => actions.attachFiles(files.map(attachmentInput)),
    onSyncFileList: (files: UploadFileInput[]) => actions.syncFileList(files.map(attachmentInput)),
    onRemoveFile: (file: { uid: string }) => actions.removeFile({ uid: String(file.uid) }),
  },
  toolbar: {
    onRefreshSessions: () => value(actions.refreshSessionList()),
    onRetryLastSend: () => actions.retry(),
    onCreateSession: () => value(actions.ensureActiveSession({ title: options.defaultSessionTitle ?? '' })),
  },
  });
};
